<template>
  <div class="wrapped-page">
    <!-- Header/Controls -->
    <div class="controls frosted" v-if="!isCapturing">
      <button class="back-btn" @click="$router.push('/dashboard')">✕</button>
      <div class="slide-indicator">
        <div v-for="i in totalSlides" :key="i" class="dot" :class="{ active: currentSlide === i-1 }"></div>
      </div>
    </div>

    <!-- Main Content -->
    <div v-if="loading" class="loading-overlay">
       <LoadingSpinner message="Dein Jahresrückblick wird vorbereitet..." />
    </div>

    <div v-else class="slides-container" @click="handleScreenClick">
      
      <div 
        v-for="(slide, index) in totalSlides" 
        :key="index"
        :class="['slide', 'slide-' + index, { 'active': currentSlide === index }]"
        :ref="'slide' + index"
      >
        <div class="slide-background"></div>
        <div class="content">
          <!-- Slide 0: Intro -->
          <template v-if="index === 0">
            <div class="pool-name-badge frosted">{{ selectedPool?.name }}</div>
            <h1 class="giant-text">
              <span class="clink-text">Clink</span>
              <span class="recap-text">Recap</span>
            </h1>
          </template>

          <!-- Slide 1: Group Stats -->
          <template v-if="index === 1">
            <h2 class="title">{{ selectedPool?.name }}<br>in Zahlen</h2>
            <div class="stats-box">
               <div class="stat-row">
                 <span class="val">{{ totalDrinks }}</span>
                 <span class="lab">Drinks</span>
               </div>
               <div class="stat-row">
                 <span class="val">{{ totalAlcohol }}g</span>
                 <span class="lab">Reiner Alkohol</span>
               </div>
            </div>
            <!----<p class="msg">Das sind ganze {{ (totalDrinks / 10).toFixed(0) }} Kästen! 🍻</p>-->
            <div class="footer-brand" v-if="isCapturing"><img src="../assets/images/clink-wide-text-white.png" alt="Clink Logo" class=""><p>clinkapp.de</p></div>
          </template>

          <!-- Slide 2: Your personal highlights -->
          <template v-if="index === 2">
            <h2 class="title">Du, {{ userProfileName || 'Legende' }}</h2>
            <div class="personal-card frosted">
              <div class="p-stat">
                <span class="p-val">{{ myStats?.count || 0 }}</span>
                <span class="p-lab">Drinks geloggt</span>
              </div>
              <div class="p-stat">
                <span class="p-val">{{ myStats?.favoriteDrink || '-' }}</span>
                <span class="p-lab">Dein Favorit</span>
              </div>
              <div class="p-stat">
                <span class="p-val">#{{ myRank }}</span>
                <span class="p-lab">Platz in der Gruppe</span>
              </div>
            </div>
            <p class="p-msg" v-if="myRank === 1">Absoluter Champion! 🏆</p>
            <p v-else class="p-msg">Klasse Leistung! 🍻</p>
            <div class="footer-brand" v-if="isCapturing"><img src="../assets/images/clink-wide-text-white.png" alt="Clink Logo" class=""><p>clinkapp.de</p></div>
          </template>

          <!-- Slide 3: Drink Distribution Visualization -->
          <template v-if="index === 3">
            <h2 class="title">Eure Favoriten</h2>
            <div class="chart-container-wrapped frosted">
              <apexchart
                type="donut"
                height="350"
                width="100%"
                :options="donutChartOptions"
                :series="donutChartSeries"
              ></apexchart>
            </div>
            <!--<p class="chart-caption">Welches Getränk hat gewonnen?</p>-->
            <div class="footer-brand" v-if="isCapturing"><img src="../assets/images/clink-wide-text-white.png" alt="Clink Logo" class=""><p>clinkapp.de</p></div>
          </template>

          <!-- Slide 4: Consumption Course Visualization -->
          <template v-if="index === 4">
            <h2 class="title">Euer Pegel im Verlauf</h2>
            <div class="chart-container-wrapped frosted area-chart-wrapped">
              <apexchart
                type="area"
                height="350"
                width="100%"
                :options="areaChartOptions"
                :series="areaChartSeries"
              ></apexchart>
            </div>
            <div class="footer-brand" v-if="isCapturing"><img src="../assets/images/clink-wide-text-white.png" alt="Clink Logo" class=""><p>clinkapp.de</p></div>
          </template>

          <!-- Slide 5: Leaderboard -->
          <template v-if="index === 5">
            <h2 class="title">Die Sieger</h2>
            <div class="leaderboard">
              <div v-for="(user, idx) in top5" :key="user.name" class="l-item frosted" :class="'rank-'+(idx+1)">
                  <span class="r-num">{{ idx+1 }}</span>
                  <span class="r-name">{{ user.name }}</span>
                  <span class="r-val">{{ user.totalAlcohol }}g</span>
              </div>
            </div>
            <div class="footer-brand" v-if="isCapturing"><img src="../assets/images/clink-wide-text-white.png" alt="Clink Logo" class=""><p>clinkapp.de</p></div>
          </template>

          <!-- Slide 6: THE SUMMARY STORY (New) -->
          <template v-if="index === 6">
            <div class="summary-header">
              <div class="pool-name-badge">Clink Recap</div>
              <h1 class="summary-title">{{ selectedPool?.name }}</h1>
            </div>
            
            <div class="summary-grid">
              <!-- Card 1: Totals -->
              <div class="summary-card frosted">
                <h3>Gesamt</h3>
                <div class="sum-row">
                  <span class="sum-val">{{ totalDrinks }}</span>
                  <span class="sum-lab">Drinks</span>
                </div>
                <div class="sum-row">
                  <span class="sum-val">{{ totalAlcohol }}g</span>
                  <span class="sum-lab">Alkohol</span>
                </div>
              </div>

              <!-- Card 2: Top Performer -->
              <div class="summary-card frosted">
                <h3>Top 5</h3>
                <div class="sum-top" v-for="(u, i) in top5" :key="u.name">
                  <span class="s-rank">#{{ i+1 }}</span>
                  <span class="s-name">{{ u.name }}</span>
                </div>
              </div>

              <!-- Card 3: Top Drinks List -->
              <div class="summary-card frosted chart-card">
                <h3>Top Getränke</h3>
                <div class="top-drinks-list">
                  <div v-for="(drink, i) in top5Drinks" :key="drink.name" class="sum-top">
                    <span class="s-rank">#{{ i+1 }}</span>
                    <span class="s-name">{{ drink.name }}</span>
                    <span class="s-count">{{ drink.count }}x</span>
                  </div>
                </div>
              </div>

              <!-- Card 4: History Chart Small -->
              <div class="summary-card frosted chart-card">
                <h3>Verlauf</h3>
                <apexchart
                  type="area"
                  height="140"
                  width="100%"
                  :options="miniAreaOptions"
                  :series="areaChartSeries"
                ></apexchart>
              </div>
            </div>

            <div class="footer-brand" v-if="isCapturing"><img src="../assets/images/clink-wide-text-white.png" alt="Clink Logo" class=""><p>clinkapp.de</p></div>
          </template>

          <!-- Slide 7: Outro -->
          <template v-if="index === 7">
            <h1 class="giant-text">DANKE<br><span>{{ selectedPool?.name }}</span></h1>
            <p class="subtitle">Bis zum nächsten Mal!</p>
            <div class="footer-brand" v-if="isCapturing"><img src="../assets/images/clink-wide-text-white.png" alt="Clink Logo" class=""><p>clinkapp.de</p></div>
          </template>
        </div>
      </div>
    </div>

    <!-- FAB for download -->
    <div class="bottom-actions" v-if="!loading && !isCapturing">
      <button class="download-btn" @click="downloadCurrentSlide" :disabled="isCapturing" v-if="currentSlide === 6">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
        {{ currentSlide === 6 ? 'Recap speichern' : 'Bild für Story speichern' }}
      </button>
      <p class="tap-hint" v-if="currentSlide === 0">Tippe links oder rechts zum Navigieren</p>
    </div>
  </div>
</template>

<script>
import AuthService from "@/services/AuthService.js";
import { toPng } from "html-to-image";
import VueApexCharts from "vue3-apexcharts";
import moment from "moment";

export default {
  name: "WrappedView",
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    return {
      loading: true,
      isCapturing: false,
      currentSlide: 0,
      totalSlides: 8,
      statistics: null,
      isDarkMode: document.documentElement.getAttribute('data-theme') === 'dark',
      // Chart Data
      donutChartSeries: [],
      donutChartOptions: {},
      areaChartSeries: [],
      areaChartOptions: {},
      miniDonutOptions: {},
      miniAreaOptions: {},
      userProfileName: null,
      userAlcoholSeries: [],
      miniUserAlcoholOptions: {},
    };
  },
  computed: {
    selectedPool() {
      return this.$store.getters.getSelectedPool;
    },
    currentUser() {
      return this.$store.getters.getUser;
    },
    totalDrinks() {
      if (!this.statistics) return 0;
      return this.statistics.drinkDistribution.reduce((acc, item) => acc + item.count, 0);
    },
    totalAlcohol() {
      if (!this.statistics) return 0;
      return Math.round(this.statistics.userAlcohol.reduce((acc, item) => acc + item.value, 0));
    },
    myStats() {
      if (!this.statistics || !this.userProfileName) return null;
      return this.statistics.userBreakdown.find(u => u.name === this.userProfileName);
    },
    myRank() {
      if (!this.statistics || !this.userProfileName) return 0;
      const sorted = [...this.statistics.userBreakdown].sort((a, b) => b.totalAlcohol - a.totalAlcohol);
      return sorted.findIndex(u => u.name === this.userProfileName) + 1;
    },
    top5() {
      if (!this.statistics) return [];
      return [...this.statistics.userBreakdown]
        .sort((a, b) => b.totalAlcohol - a.totalAlcohol)
        .slice(0, 5);
    },
    top5Drinks() {
      if (!this.statistics) return [];
      return [...this.statistics.drinkDistribution]
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
    }
  },
  async mounted() {
    if (!this.selectedPool) {
      this.$router.push("/");
      return;
    }
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const poolId = this.selectedPool.pool_id || this.selectedPool.id;
        
        const [statsResponse, profileResponse] = await Promise.all([
          AuthService.getPoolDashboardStatistics({ pool_id: poolId }),
          AuthService.getUserProfileFromPool({ pool_id: poolId })
        ]);

        if (statsResponse.success) {
          this.statistics = statsResponse.msg;
          this.prepareChartData();
        }
        
        if (profileResponse.success && profileResponse.msg.length > 0) {
          this.userProfileName = profileResponse.msg[0].name;
        }
      } catch (e) {
        console.error("Error fetching wrapped data:", e);
      } finally {
        this.loading = false;
      }
    },
    prepareChartData() {
      if (!this.statistics) return;

      const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';
      const textColor = isDarkMode ? '#FFFFFF' : '#000000';
      const gridColor = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';

      // 1. Donut Chart
      this.donutChartSeries = this.statistics.drinkDistribution.map(item => item.count);
      const commonDonutOptions = {
        labels: this.statistics.drinkDistribution.map(item => item.name),
        chart: { background: 'transparent', toolbar: { show: false } },
        colors: ["#184e77", "#d9ed92", "#99d98c", "#34a0a4", "#52b69a"],
        stroke: { show: false },
        dataLabels: { enabled: false },
        tooltip: { theme: isDarkMode ? 'dark' : 'light' }
      };

      this.donutChartOptions = {
        ...commonDonutOptions,
        legend: {
          show: true,
          position: 'bottom',
          labels: { colors: textColor, useSeriesColors: false },
          fontFamily: 'inherit',
          fontWeight: 600,
        },
        dataLabels: { enabled: true, dropShadow: { enabled: false } },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                name: { color: textColor },
                value: { color: textColor, fontWeight: 800 },
                total: { show: true, label: 'TOTAL', color: textColor, fontWeight: 900 }
              }
            }
          }
        },
      };

      this.miniDonutOptions = {
        ...commonDonutOptions,
        legend: { show: false },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
              labels: {
                show: true,
                name: { show: false },
                value: { show: true, color: textColor, fontSize: '14px', fontWeight: 800, offsetY: 5 },
                total: { show: true, label: '', color: textColor }
              }
            }
          }
        }
      };

      // 2. Area Chart
      const areaData = this.statistics.cumulativeAlcohol.map(item => ({
        x: moment(item.timestamp).valueOf(),
        y: item.value
      }));

      this.areaChartSeries = [{ name: "Alkohol", data: areaData }];
      const commonAreaOptions = {
        chart: { type: "area", toolbar: { show: false }, zoom: { enabled: false }, background: 'transparent' },
        colors: [isDarkMode ? "#FFFFFF" : "#000000"],
        stroke: { curve: "smooth", width: 4 },
        fill: {
          type: "gradient",
          gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0.1 },
        },
        yaxis: { show: false },
        grid: { show: false },
        dataLabels: { enabled: false },
        tooltip: { theme: isDarkMode ? 'dark' : 'light' }
      };

      this.areaChartOptions = {
        ...commonAreaOptions,
        xaxis: {
          type: "datetime",
          labels: { style: { colors: textColor, fontWeight: 600 }, datetimeUTC: false, format: 'dd.MM.' },
          axisBorder: { show: false },
          axisTicks: { show: false }
        },
      };

      this.miniAreaOptions = {
        ...commonAreaOptions,
        chart: { 
          ...commonAreaOptions.chart, 
          sparkline: { enabled: true },
          padding: { left: 0, right: 0 }
        },
        stroke: { curve: "smooth", width: 2 },
        xaxis: { show: false, labels: { show: false }, axisBorder: { show: false }, axisTicks: { show: false } },
      };

      // 3. User Alcohol Bar Chart (for Summary)
      const topUsers = this.statistics.userAlcohol.slice(0, 5);
      this.userAlcoholSeries = [{
        name: "Alkohol (g)",
        data: topUsers.map(u => u.value)
      }];

      this.miniUserAlcoholOptions = {
        chart: { type: "bar", toolbar: { show: false }, background: 'transparent' },
        plotOptions: { 
          bar: { 
            horizontal: true, 
            borderRadius: 4, 
            barHeight: '70%',
            dataLabels: { position: 'end' }
          }
        },
        colors: ["#52b69a"],
        xaxis: { 
          categories: topUsers.map(u => u.name),
          labels: { show: false }, 
          axisBorder: { show: false }, 
          axisTicks: { show: false } 
        },
        yaxis: { 
          labels: { 
            style: { colors: textColor, fontSize: '10px', fontWeight: 600 } 
          } 
        },
        grid: { show: false },
        dataLabels: { 
          enabled: true, 
          formatter: (v) => v + "g", 
          style: { colors: [textColor], fontSize: '9px' },
          offsetX: 20
        },
        tooltip: { enabled: false }
      };
    },
    handleScreenClick(e) {
      const x = e.clientX;
      const width = window.innerWidth;
      if (x < width / 3) {
        this.prevSlide();
      } else {
        this.nextSlide();
      }
    },
    nextSlide() {
      if (this.currentSlide < this.totalSlides - 1) {
        this.currentSlide++;
      } else {
        this.currentSlide = 0;
      }
    },
    prevSlide() {
      if (this.currentSlide > 0) {
        this.currentSlide--;
      }
    },
    async downloadCurrentSlide() {
      this.isCapturing = true;
      try {
        const slideEl = this.$refs['slide' + this.currentSlide][0];
        // Short delay to ensure any layout shifts/chart animations are done
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const dataUrl = await toPng(slideEl, {
          pixelRatio: 3, // Capture at 3x for high resolution
          style: {
            transform: 'none',
            left: '0',
            top: '0',
            opacity: '1',
            pointerEvents: 'none',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            width: '375px', // Standard mobile width
            height: '667px', // 16:9 aspect ratio standard (Story format)
            margin: '0',
            padding: '40px 20px', // Fixed padding for the export element
            backgroundSize: 'cover'
          }
        });
        
        const link = document.createElement('a');
        link.download = `clink-wrapped-summary.png`;
        link.href = dataUrl;
        link.click();
      } catch (error) {
        console.error("Could not capture slide:", error);
      } finally {
        this.isCapturing = false;
      }
    }
  }
};
</script>

<style scoped>
.wrapped-page {
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  position: relative;
  background: var(--background) url(@/assets/images/clink-bg.png) no-repeat !important;
  background-size: cover !important;
  background-position: center !important;
  color: var(--primary-color);
  font-family: var(--font);
}

.loading-overlay {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
}

.controls {
  position: absolute;
  top: 60px;
  left: 20px;
  right: 20px;
  height: 40px;
  z-index: 100;
  display: flex;
  align-items: center;
  padding: 0 15px;
  border-radius: 20px;
}

.back-btn {
  background: none;
  border: none;
  color: var(--primary-color);
  font-size: 24px;
  cursor: pointer;
  padding: 5px;
}

.slide-indicator {
  flex-grow: 1;
  display: flex;
  gap: 6px;
  padding: 0 15px;
}

.dot {
  flex-grow: 1;
  height: 4px;
  background: var(--border-color);
  border-radius: 2px;
}

.dot.active {
  background: var(--primary-color);
}

.slides-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.5s ease;
  display: flex;
  flex-direction: column;
  padding: 10vh 6vw;
  box-sizing: border-box;
}

.slide.active {
  opacity: 1;
  pointer-events: auto;
}



.slide-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  background: var(--background) url(@/assets/images/clink-bg.png) no-repeat !important;
  background-size: cover !important;
  background-position: center !important;
}

.content {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.pool-name-badge {
  padding: 8px 16px;
  border-radius: 50px;
  font-weight: 700;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 0.9rem;
}

.giant-text {
  font-size: 4.5rem;
  line-height: 0.9;
  font-weight: 900;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: -2px;
  animation: chromatic-edge 0.6s infinite steps(1);
  position: relative;
}

@keyframes chromatic-edge {
  0% { text-shadow: 1.5px 0 rgba(255,0,255,0.6), -1.5px 0 rgba(0,255,255,0.6); transform: translate(0); }
  20% { text-shadow: -1.5px 0 rgba(255,0,255,0.6), 1.5px 0 rgba(0,255,255,0.6); transform: translate(1px, 0); }
  40% { text-shadow: 1px 0.5px rgba(255,0,0,0.5), -1px -0.5px rgba(0,0,255,0.5); transform: translate(-1px, 0); }
  60% { text-shadow: -1px -0.5px rgba(255,0,0,0.5), 1px 0.5px rgba(0,0,255,0.5); transform: translate(0); }
  80% { text-shadow: 2px 0 rgba(255,0,255,0.5), -2px 0 rgba(0,255,255,0.5); }
  100% { text-shadow: 1.5px 0 rgba(255,0,255,0.6), -1.5px 0 rgba(0,255,255,0.6); }
}

.giant-text span {
  display: block;
}

.clink-text {
  display: inline-block;
  animation: fly-in-left 1s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.recap-text {
  display: inline-block;
  animation: fly-in-right 1s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

@keyframes fly-in-left {
  0% { transform: translateX(-200%); opacity: 0; }
  100% { transform: translateX(0); opacity: 1; }
}

@keyframes fly-in-right {
  0% { transform: translateX(200%); opacity: 0; }
  100% { transform: translateX(0); opacity: 1; }
}

.footer-brand {
  position: absolute;
  bottom: 100px;
  height: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.footer-brand img {
  height: 70px;
}

.footer-brand p {
  margin: 0;
}

.title {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 30px;
  line-height: 1.1;
}

.stats-box {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  margin-bottom: 30px;
}

.stat-row {
  display: flex;
  flex-direction: column;
}

.stat-row .val {
  font-size: 4rem;
  font-weight: 900;
  line-height: 1;
}

.stat-row .lab {
  font-size: 1.1rem;
  opacity: 0.8;
  font-weight: 600;
  text-transform: uppercase;
}

.msg {
  font-size: 1.4rem;
  font-weight: 600;
  opacity: 0.9;
}

.personal-card {
  width: 100%;
  max-width: 90%;
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.p-stat {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
}

.p-val {
  font-size: 2.5rem;
  font-weight: 900;
  line-height: 1;
}

.p-lab {
  font-size: 0.9rem;
  opacity: 0.7;
  font-weight: 700;
  text-transform: uppercase;
}

.p-msg {
  margin-top: 30px;
  font-size: 1.5rem;
  font-weight: 700;
}

.leaderboard {
  width: 100%;
  max-width: 90%;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.l-item {
  display: flex;
  align-items: center;
  padding: 20px;
}

.r-num {
  font-size: 1.5rem;
  font-weight: 900;
  margin-right: 20px;
  width: 30px;
  text-align: center;
}

.r-name {
  flex-grow: 1;
  text-align: left;
  font-weight: 700;
  font-size: 1.2rem;
}

.r-val {
  font-weight: 800;
  opacity: 0.8;
}

.rank-1 { border: 2px solid var(--primary-color) !important; }

.bottom-actions {
  position: absolute;
  bottom: 40px;
  left: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

.download-btn {
  background: var(--primary-color);
  color: var(--background);
  border: none;
  padding: 16px 32px;
  border-radius: 50px;
  font-weight: 800;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 10px 20px rgba(0,0,0,0.1);
}

.tap-hint {
  font-size: 0.85rem;
  opacity: 0.6;
  font-weight: 600;
}

.subtitle {
  font-size: 1.3rem;
  font-weight: 600;
  margin-top: 20px;
  opacity: 0.8;
}

/* Wrapped Chart Styles */
.chart-container-wrapped {
  width: 100%;
  max-width: 90%;
  padding: 20px;
}

.area-chart-wrapped {
  padding: 20px 10px 10px 10px;
}

.chart-caption {
  margin-top: 20px;
  font-size: 1.2rem;
  font-weight: 700;
  opacity: 0.8;
}

/* Summary Slide Styles */
.summary-header {
  margin-bottom: 30px;
}

.summary-header .pool-name-badge {
  margin-bottom: 0;
  text-align: left;
  padding: 0;
}

.summary-title {
  font-size: 2.2rem;
  font-weight: 900;
  text-transform: uppercase;
  margin: 0;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: auto auto;
  gap: 10px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  margin: 0 auto;
}

.summary-card {
  min-width: 0;
  overflow: hidden;
  padding: 10px;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  text-align: center;
}

.summary-card h3 {
  margin: 0 0 10px 0;
  font-size: 1rem;
  text-align: left;
  align-self: baseline;
  text-transform: uppercase;
  letter-spacing: 1px;
  opacity: 0.6;
}

.sum-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 5px;
}

.sum-val {
  font-size: 1.8rem;
  font-weight: 900;
  line-height: 1;
}

.sum-lab {
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
}

.sum-top {
  display: flex;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 700;
  width: 100%;
}

.s-rank { 
  opacity: 0.5;
  width: 20px;
}

.s-name {
  text-align: left;
  flex: 1;
}

.s-count {
  margin-left: auto;
  opacity: 0.7;
}

.top-drinks-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.chart-card {
  padding: 10px;
  justify-content: flex-start;
}

.summary-brand {
  margin-top: 40px;
  font-weight: 900;
  font-size: 1.8rem;
  opacity: 0.3;
}

[data-theme='dark'] .frosted {
  background: rgba(0, 0, 0, 0.3);
}

@media (max-height: 700px) {
  .giant-text { font-size: 3.5rem; }
  .title { font-size: 2rem; }
  .stat-row .val { font-size: 3rem; }
  .summary-title { font-size: 1.8rem; }
  .summary-grid { gap: 10px; }
}
</style>
